import {
  escapeHtml,
  resolveIconPath,
  sanitizeHttpUrl,
  sortByImportanceDesc
} from "./utils/spot_utils";
import { logger } from "./utils/logger";

export function buildBoundsParams(map, tags) {
  const bounds = map.getBounds();

  return new URLSearchParams({
    n: bounds.getNorth().toFixed(6),
    s: bounds.getSouth().toFixed(6),
    w: bounds.getWest().toFixed(6),
    e: bounds.getEast().toFixed(6),
    tags
  });
}

export function createSpotsListController({
  map,
  spotsPath,
  infoIconSize,
  otherTagsById,
  wirelessTagsById,
  elements,
  powerSupplyTags,
  fetchImpl = fetch,
  imageBaseUrl = ""
}) {
  let latestRequestId = 0;

  const setVisibility = (element, visible) => {
    element?.classList.toggle("hidden", !visible);
  };

  const iconItemHtml = (src, label) =>
    `<li class="inline-block" title="${escapeHtml(label)}"><img src="${escapeHtml(src)}" alt="${escapeHtml(label)}" style="width:${infoIconSize}px;height:${infoIconSize}px;object-fit:contain" /></li>`;

  const buildSpotIconsHtml = (categoryImage, categoryAlt, infoTagsWithImage) => {
    const iconItems = [];

    if (categoryImage) {
      iconItems.push(iconItemHtml(categoryImage, categoryAlt));
    }

    infoTagsWithImage.forEach((tag) => {
      iconItems.push(iconItemHtml(tag.image, tag.name || ""));
    });

    if (!iconItems.length) return "";

    return `<ul class="flex flex-wrap gap-1 list-none p-0 m-0">${iconItems.join("")}</ul>`;
  };

  const resolveTagsWithImage = (tagObjects, tagsById) => {
    if (!Array.isArray(tagObjects)) return [];

    const resolved = tagObjects
      .map((tag) => {
        const resolvedTag = tag?.id == null ? tag : (tagsById[String(tag.id)] || tag);
        if (!resolvedTag?.image) return null;

        return {
          ...resolvedTag,
          image: resolveIconPath(resolvedTag.image, infoIconSize, imageBaseUrl)
        };
      })
      .filter((tag) => tag?.image);

    return sortByImportanceDesc(resolved);
  };

  const renderSpotCard = (spot) => {
    const categoryImage = spot.prime_category?.image
      ? resolveIconPath(spot.prime_category.image, infoIconSize, imageBaseUrl)
      : null;
    const categoryAlt = spot.prime_category?.name || "カテゴリ";

    const otherTagsWithImage = resolveTagsWithImage(spot.tags, otherTagsById);
    const wirelessTagsWithImage = resolveTagsWithImage(spot.wireless, wirelessTagsById);
    const infoTagsWithImage = [...otherTagsWithImage, ...wirelessTagsWithImage];

    const iconsHtml = buildSpotIconsHtml(categoryImage, categoryAlt, infoTagsWithImage);

    const address = escapeHtml(spot.address || "住所情報は準備中です");
    const title = escapeHtml(spot.title || "名称未設定スポット");
    const detailUrl = sanitizeHttpUrl(spot.mo_url || spot.url);
    const titleHtml = detailUrl
      ? `<a href="${escapeHtml(detailUrl)}" class="hover:underline focus:underline" target="_blank" rel="noopener noreferrer">${title}</a>`
      : title;
    const detailHtml = detailUrl
      ? `<a href="${escapeHtml(detailUrl)}" class="mt-4 inline-flex text-sm font-medium text-[#337ab7]" target="_blank" rel="noopener noreferrer">詳細を見る</a>`
      : "";

    return `
      <article class="h-full rounded-2xl bg-white px-4 py-5 shadow-[0_8px_24px_rgba(25,28,32,0.06)] ring-1 ring-slate-900/10">
        <h3 class="text-[1.15rem] leading-7 font-semibold tracking-tight text-[#337ab7]">${titleHtml}</h3>
        ${iconsHtml ? `<div class="mt-4">${iconsHtml}</div>` : ""}
        <p class="mt-4 text-sm leading-6 text-slate-600">${address}</p>
        ${detailHtml}
      </article>
    `;
  };

  const setSummary = (message) => {
    if (elements.summary) elements.summary.textContent = message;
  };

  const renderSpotsList = (spots, status) => {
    if (!elements.list) return;

    if (!spots.length) {
      elements.list.innerHTML = "";
      setVisibility(elements.empty, true);
      setSummary("この表示範囲では店舗が見つかりませんでした");
      return;
    }

    setVisibility(elements.empty, false);
    elements.list.innerHTML = spots.map((spot) => renderSpotCard(spot)).join("");

    if (status === "TOO_MUCH_SPOTS") {
      setSummary(`${spots.length}件を表示中 - 件数が多いため地図を拡大してください`);
    } else {
      setSummary(`${spots.length}件を表示中`);
    }
  };

  const refreshVisibleSpots = async () => {
    if (!spotsPath) return;

    const requestId = ++latestRequestId;
    setVisibility(elements.loading, true);
    setVisibility(elements.error, false);
    setVisibility(elements.empty, false);

    try {
      const response = await fetchImpl(`${spotsPath}?${buildBoundsParams(map, powerSupplyTags).toString()}`, {
        headers: { Accept: "application/json" }
      });

      if (!response.ok) throw new Error(`Failed to fetch spots: ${response.status}`);

      const payload = await response.json();
      if (requestId !== latestRequestId) return;

      renderSpotsList(Array.isArray(payload.results) ? payload.results : [], payload.status);
    } catch (error) {
      if (requestId !== latestRequestId) return;

      logger.error(error);
      if (elements.list) elements.list.innerHTML = "";
      setVisibility(elements.error, true);
      setVisibility(elements.empty, false);
      setSummary("一覧の取得に失敗しました");
    } finally {
      if (requestId === latestRequestId) {
        setVisibility(elements.loading, false);
      }
    }
  };

  return {
    refreshVisibleSpots,
    renderSpotsList
  };
}
